<template>
  <section
    :class="carouselSectionClass"
    class="container-fluid d-flex flex-column py-space-lg px-auto"
    :aria-label="title + ' Carousel'">
    <!-- Header Container -->
    <div
      :class="headerClass"
      class="col-12 col-md-8 d-flex flex-column align-self-center">
      <h2 class="mb-space-sm" :class="titleClass">
        {{ title }}
      </h2>
      <div
        v-if="displayUnderline"
        :style="underlineStyle"
        class="carousel-underline mb-space-sm"></div>
      <div
        v-if="description"
        v-html="description"
        class="carousel-description mb-space-sm"
        :class="descriptionClass"></div>
    </div>

    <!-- Slide Container -->
    <div
      class="container slide-container gx-0 mb-space-sm px-0 mb-md-space-xl px-md-space-xs no-overflow-anchor"
      ref="slideContainer">
      <!-- slot for the slide -->
      <transition
        :name="transitionDirection"
        @enter="entering"
        @after-enter="resetHeight"
        @before-leave="leaving">
        <div
          :key="'slide' + keyPrefix + currentSlide"
          class="no-overflow-anchor">
          <slot
            :slideIndex="currentSlide"
            :slide="slides[currentSlide]"
            :handleCtaClick="handleCtaClick"
            :handleCardClick="handleCardClick"
            :handleCardEnter="handleCardEnter"
            :handleIconClick="handleIconClick"></slot>
        </div>
      </transition>
    </div>

    <!-- controls Container -->
    <div
      v-if="slides.length > 1"
      class="text-center"
      aria-label="carousel buttons">
      <div
        v-if="displayCarouselIndicator"
        class="mb-space-sm mb-md-space-md no-overflow-anchor">
        <button
          :aria-label="'select slide ' + (index + 1)"
          :class="indicatorColorSelector(index)"
          class="rounded-circle indicator-dots no-overflow-anchor"
          v-for="(item, index) in slides"
          :key="index"
          :disabled="currentSlide === index"
          @click="handleIndicatorClick(index)"></button>
      </div>
      <div class="d-flex justify-content-center no-overflow-anchor">
        <button
          aria-label="previous slide"
          class="carousel-control me-space-xs"
          :disabled="currentSlide === 0"
          @click="handleClickPrev">
          <img :src="left" alt="prev slide" />
        </button>
        <button
          aria-label="next slide"
          class="carousel-control no-overflow-anchor"
          :disabled="currentSlide === slides.length - 1"
          @click="handleClickNext">
          <img :src="right" alt="next slide" />
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts" generic="T extends unknown">
import { computed, onMounted, ref } from "vue";
import left from "./assets/left.svg?url";
import right from "./assets/right.svg?url";

interface Props {
  /** Follow the slide conventions of the component you're using */
  slides: T[];
  sectionYPadding?:
    | "0"
    | "xxxs"
    | "xxs"
    | "xs"
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "xxl"
    | "xxxl";
  sectionBackgroundVariant?: string;
  headerAlignment?: "left" | "right" | "center";
  title: string;
  titleVariant?: string;
  titleSize?: "small" | "medium" | "large" | "xl";
  description?: string;
  descriptionVariant?: string;
  descriptionSize?: "small" | "medium" | "large" | "xl";
  displayCarouselIndicator?: boolean;
  displayUnderline?: boolean;
  underlineColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  sectionYPadding: "xxl",
  sectionBackgroundVariant: "white",
  headerAlignment: "center",
  titleVariant: "dark-3",
  titleSize: "medium",
  descriptionVariant: "dark-3",
  descriptionSize: "medium",
  displayCarouselIndicator: true,
  displayUnderline: true,
  underlineColor: "var( --rds-secondary, #ffc627)",
});

// events
const emits = defineEmits<{
  /**
   *  emitted when next slide button is clicked.
   */
  (e: "nextSlideClick", newSlideIndex: number): void;
  /**
   *  emitted when previous slide button is clicked.
   */
  (e: "previousSlideClick", newSlideIndex: number): void;
  /**
   *  emitted when indicator button is clicked.
   */
  (e: "indicatorClick", newSlideIndex: number): void;
  /*
       event will be Pointer Event Object or Mouse Event Object
       example: PointerEvent {isTrusted: true, PointerId: 1, width: 1, ...}
      */
  (e: "cardClick", event: Event): void;
  /*
       event will be Pointer Event Object or Mouse Event Object
       example: PointerEvent {isTrusted: true, PointerId: 1, width: 1, ...}
      */
  (e: "ctaClick", event: Event): void;
  /*
       event will be Mouse Event Object
       example: PointerEvent {isTrusted: true, PointerId: 1, width: 1, ...}
      */
  (e: "cardEnter", event: Event): void;
  /*
       event will be Pointer Event Object or Mouse Event Object
       example: PointerEvent {isTrusted: true, PointerId: 1, width: 1, ...}
      */
  (e: "iconClick", event: Event): void;
}>();

const ctaAlignMap = {
  left: "start",
  center: "center",
  right: "end",
};

const keyPrefix = ref<string | null>();
const slideContainer = ref<HTMLElement>();
const currentSlide = ref<number>(0);
const transitionDirection = ref<"slide-previous" | "slide-next">(
  "slide-previous"
);

const carouselSectionClass = computed(() => {
  return `py-md-${
    props.sectionYPadding == "0"
      ? props.sectionYPadding
      : "space-" + props.sectionYPadding
  } bg-${props.sectionBackgroundVariant} `;
});

const descriptionClass = computed(() => {
  return `fs-${props.descriptionSize} text-${props.descriptionVariant}`;
});

const titleClass = computed(() => {
  return `h2-${props.titleSize} text-${props.titleVariant}`;
});

const headerClass = computed(() => {
  let classNames = ``;
  classNames += ` align-items-${ctaAlignMap[props.headerAlignment]} `;

  classNames += ` text-${ctaAlignMap[props.headerAlignment]}`;

  return classNames;
});

const underlineStyle = computed(() => {
  return {
    "background-color": props.underlineColor,
  };
});

const handleIndicatorClick = (index: number) => {
  currentSlide.value >= index
    ? (transitionDirection.value = "slide-previous")
    : (transitionDirection.value = "slide-next");
  currentSlide.value = index;
  emits("indicatorClick", index);
};

const handleClickNext = () => {
  if (currentSlide.value !== props.slides.length - 1) {
    transitionDirection.value = "slide-next";
    currentSlide.value++;
    emits("nextSlideClick", currentSlide.value);
  }
};
const handleClickPrev = () => {
  if (currentSlide.value !== 0) {
    transitionDirection.value = "slide-previous";
    currentSlide.value--;
    emits("previousSlideClick", currentSlide.value);
  }
};

const indicatorColorSelector = (index: number) => {
  if (index === currentSlide.value) {
    return props.sectionBackgroundVariant == "dark-3"
      ? `bg-dark-1`
      : `bg-dark-3`;
  } else {
    return props.sectionBackgroundVariant == "dark-3"
      ? `bg-light-2`
      : `bg-light-5`;
  }
};

const handleCtaClick = (event: Event) => {
  emits("ctaClick", event);
};

const handleCardClick = (event: Event) => {
  emits("cardClick", event);
};

const handleCardEnter = (event: Event) => {
  emits("cardEnter", event);
};

const handleIconClick = (event: Event) => {
  emits("iconClick", event);
};

// Sets the height to the leaving element then sets to entering element and lastly, removes the inline height value
const entering = (el: HTMLElement | Element) => {
  if (el instanceof HTMLElement && slideContainer.value) {
    // set the height to the incoming element
    slideContainer.value.style.height = el.clientHeight + "px";
  }
};
const leaving = (el: HTMLElement | Element) => {
  if (el instanceof HTMLElement && slideContainer.value) {
    // set the height to the leaving element
    slideContainer.value.style.height = el.clientHeight + "px";
  }
};

const resetHeight = () => {
  slideContainer.value!.style.height = "";
};

onMounted(() => {
  keyPrefix.value = Math.floor(Math.random() * 10000) + "sts";
});
</script>

<style lang="scss" scoped>
.carousel-underline {
  width: 25%;
  max-width: 10.5rem;
  min-width: 65px;
  height: 0.3rem;
}

.indicator-dots {
  width: 16px;
  height: 16px;
  padding: 0;
  margin: 0 8px;
  border: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.carousel-control {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--rds-light-3, #e8e8e8);
  border: 1px solid var(--rds-light-4, #d0d0d0);
  cursor: pointer;
}

.carousel-control:focus,
.indicator-dots:focus {
  outline: 2px solid var(--rds-dark-3, #191919);
}

.bg-dark-3 .carousel-control:focus,
.bg-dark-3 .indicator-dots:focus {
  outline: 2px solid var(--rds-light-2, #f1f1f1);
  outline-offset: 0.2em;
}

.indicator-dots:disabled {
  opacity: 1;
  cursor: auto;
  outline: none !important;
}

button:disabled {
  opacity: 0.5;
  cursor: auto;
  outline: none !important;
}

.slide-container {
  position: relative;
  overflow: hidden;
  transition: height 400ms;
}

.no-overflow-anchor {
  overflow-anchor: none;
}

/* transition animations start */
.slide-previous-enter-active,
.slide-previous-leave-active,
.slide-next-enter-active,
.slide-next-leave-active {
  transition: transform 400ms cubic-bezier(0.165, 0.84, 0.44, 1) 0s;
}

.slide-previous-leave-active,
.slide-next-leave-active {
  position: absolute;
  top: 0;
  left: 0;
}

.slide-previous-enter-from {
  transform: translate3d(-100%, 0px, 0px);
}
.slide-previous-leave-to {
  transform: translate3d(100%, 0px, 0px);
}
.slide-next-enter-from {
  transform: translate3d(100%, 0px, 0px);
}
.slide-next-leave-to {
  transform: translate3d(-100%, 0px, 0px);
}

@media (prefers-reduced-motion) {
  .slide-previous-enter-active,
  .slide-previous-leave-active,
  .slide-next-enter-active,
  .slide-next-leave-active {
    transition: transform 2s cubic-bezier(0.165, 0.84, 0.44, 1) 0s;
  }
}
/* transition animations end */
</style>
